import { createEntityAdapter, createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
  checkCCTVChannelRound,
  commentSituation,
  freeDeviceHistory,
  getSituationLogs,
  holdSituation,
  openCallWindow,
  openCctvCamera,
  openSituationChat,
  processSituation,
  processSituations,
  sendSituationOpenedNotification,
} from '../thunks/monitoring';

import {
  getSituationById,
  getSituationsByIds,
  getSituationsByIdsMinimal,
} from '../thunks/situation';
const situationsAdapter = createEntityAdapter();

const INITIAL_STATE = situationsAdapter.getInitialState();

const situationsSlice = createSlice({
  name: 'situations',
  initialState: INITIAL_STATE,
  reducers: {
    addSituation: (state, action) => {
      const {
        action: situationAction,
        id,
        log: _incomingLog,
        ...rest
      } = action.payload;
      if (situationAction === 'delete') {
        situationsAdapter.removeOne(state, id);
        return;
      }

      const currentSituation = state.entities[id];

      const situationToUpsert = {
        id,
        ...(currentSituation !== null ? currentSituation : {}),
        ...rest,
        log: currentSituation?.log ?? [],
        onHold: currentSituation?.onHold ?? rest.onHold ?? false,
        roundId:
          rest.roundId != null
            ? rest.roundId
            : (currentSituation?.roundId ?? null),
      };

      situationsAdapter.upsertOne(state, { ...situationToUpsert });
    },
    setSituationHoldStatus: (state, action) => {
      const { situationId, onHold } = action.payload;

      situationsAdapter.updateOne(state, {
        id: situationId,
        changes: { onHold },
      });
    },
    resetSituations: () => INITIAL_STATE,
    createSituationChat: (state, action) => {
      const { situationId, conversationId } = action.payload;
      situationsAdapter.updateOne(state, {
        id: situationId,
        changes: {
          conversationId,
        },
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSituationById.fulfilled, (state, action) => {
        const situation = action.payload;

        if (!situation) return;

        const currentSituation = state.entities[situation.id];

        const formattedSituation = {
          id: situation.id,
          conversationId: situation.conversationId,
          assignedTo: situation.assignedTo,
          log: Array.isArray(situation.log?.data)
            ? situation.log.data
            : (currentSituation?.log ?? []),
          deviceHistoriesCount:
            situation.deviceHistoriesCount ??
            currentSituation?.deviceHistoriesCount ??
            0,
          highPriorityEvent:
            currentSituation?.highPriorityEvent ??
            situation.highPriorityEvent ??
            null,
          device: situation.device ?? currentSituation?.device ?? null,
          onHold: currentSituation?.onHold ?? situation.onHold ?? false,
        };

        situationsAdapter.upsertOne(state, formattedSituation);
      })
      .addCase(freeDeviceHistory.fulfilled, (state, action) => {
        if (!action.payload?.result || !action.payload.id) return;

        const { result } = action.payload;

        // No-op free (already freed by a concurrent/duplicate call): situation is null
        if (!result.situation) return;

        const situation = state.entities[result.situation.situationId];

        if (!situation || !result.situation.situationId) return;

        situation.deviceHistoriesCount = result.situation.deviceHistoriesCount;

        situation.highPriorityEvent = {
          id: result.situation.highPriorityEventId,
          code: result.situation.highPriorityEventCode,
          technicalDescription:
            result.situation.highPriorityEventTechnicalDescription,
          color: result.situation.highPriorityEventColor,
          priority: result.situation.highPriorityEventPriority,
          eventGroup: result.situation.highPriorityEventGroupId
            ? {
                id: result.situation.highPriorityEventGroupId,
                name: result.situation.highPriorityEventGroupName,
              }
            : null,
          receivedAt: result.situation.highPriorityEventReceivedAt,
          generatedAt: result.situation.highPriorityEventGeneratedAt,
        };

        // Rebuild device with nested structure, preserving lastDevicePosition and devicePlaces
        situation.device = {
          ...situation.device,
          id: result.situation.deviceId,
          name: result.situation.deviceName,
          uniqueId: result.situation.deviceUniqueId,
          workspacePrefix: {
            prefix: result.situation.deviceWorkspacePrefixPrefix,
          },
        };

        situationsAdapter.upsertOne(state, situation);
      })
      .addCase(processSituation.fulfilled, (state, action) => {
        const { id: situationId } = action.payload;

        situationsAdapter.removeOne(state, situationId);
      })
      .addCase(processSituations.fulfilled, (state, action) => {
        const { successfulIds } = action.payload;

        if (successfulIds?.length > 0) {
          situationsAdapter.removeMany(state, successfulIds);
        }
      })
      .addCase(getSituationsByIds.fulfilled, (state, action) => {
        const situations = action.payload;

        situationsAdapter.upsertMany(state, situations);
      })
      .addCase(getSituationsByIdsMinimal.fulfilled, (state, action) => {
        const situations = action.payload;

        if (!Array.isArray(situations)) return;

        const merged = situations.map((situation) => {
          const currentSituation = state.entities[situation.id];
          if (!currentSituation) return situation; // legit back-fill

          // Real-time subscription already inserted richer data: don't
          // degrade it with the minimal result (no highPriorityEvent, etc.).
          return {
            ...currentSituation,
            ...situation,
            device: situation.device ?? currentSituation.device,
            highPriorityEvent: currentSituation.highPriorityEvent ?? null,
            deviceHistoriesCount:
              currentSituation.deviceHistoriesCount ??
              situation.deviceHistoriesCount ??
              0,
            log: currentSituation.log ?? situation.log ?? [],
            onHold: currentSituation.onHold ?? situation.onHold ?? false,
          };
        });

        situationsAdapter.upsertMany(state, merged);
      })
      .addMatcher(
        isAnyOf(
          sendSituationOpenedNotification.fulfilled,
          commentSituation.fulfilled,
          getSituationLogs.fulfilled,
          openSituationChat.fulfilled,
          openCallWindow.fulfilled,
          openCctvCamera.fulfilled,
          checkCCTVChannelRound.fulfilled,
          holdSituation.fulfilled
        ),
        (state, action) => {
          const { id: situationId, log } = action.payload;

          const situationToWriteLog = state.entities[situationId];

          if (situationToWriteLog) {
            situationToWriteLog.log = Array.isArray(log?.data)
              ? log.data
              : (situationToWriteLog.log ?? []);
          }
        }
      );
  },
});

export const {
  addSituation,
  resetSituations,
  createSituationChat,
  setSituationHoldStatus,
} = situationsSlice.actions;

export const situationsAdapterSelectors = situationsAdapter.getSelectors(
  (state) => state.situations
);

export default situationsSlice.reducer;
